import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, unref as _unref, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "xfield form-field" }
const _hoisted_2 = {
  for: "nationalIdNumber",
  class: "cuscd-form-field-label"
}
const _hoisted_3 = {
  key: 0,
  class: "required"
}
const _hoisted_4 = { class: "input-wrapper" }
const _hoisted_5 = ["disabled", "value"]
const _hoisted_6 = ["disabled"]

import { computed, ref, watch, toRef, onMounted } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import useValidation from "@/use/validation";
import type { FieldSchema } from "@/models/form";
import {
  NationalIdNumberTypeIds,
  NationalIdNumberTypeDefinitions,
} from "@/models/nationalIdNumberTypes";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";


export default /*@__PURE__*/_defineComponent({
  __name: 'NationalIdNumberInput',
  props: {
    nationalIdNumberTypeId: {},
    modelValue: {},
    disabled: { type: Boolean },
    rules: {},
    schema: {},
    errors: {},
    showItinIndicator: { type: Boolean, default: false },
    useStars: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "update:itinIndicator"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const modelValue = toRef(props, "modelValue");

const emit = __emit;

const { isNationalIdNumberMasked } = useValidation();

const isMasked = ref<boolean>(true);
const isFocused = ref<boolean>(false);
const showNationalIdNumberToggle = ref<boolean>(false);
const itinIndicator = ref<boolean>(false);

const maskedPart = computed<string>(() => {
  return props.useStars ? "***-**-" : "XXX-XX-";
});

const toggleIcon = computed<string>(() => (isMasked.value ? "eye" : "eye-slash"));

const displayValue = computed<string | undefined | unknown>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");

  if (props.disabled) {
    if (cleanedValue) {
      if (cleanedValue.length === 4) {
        return maskedPart.value + cleanedValue;
      }

      if (cleanedValue.length === 9) {
        const visiblePart = cleanedValue.slice(-4);
        return maskedPart.value + visiblePart;
      }
    }
  }

  if (isNationalIdNumberMasked(props.modelValue || "") || !isFocused.value) {
    return props.modelValue;
  }

  return value.value;
});

const rawValue = computed<string | undefined>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");
  return cleanedValue ? cleanedValue : undefined;
});
const maskedValue = computed<string>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");
  return cleanedValue ? `XXX-XX-${cleanedValue.slice(-4)}` : "";
});

const nationalIdNumberLabel = computed<string>(() => {
  return props.nationalIdNumberTypeId == NationalIdNumberTypeIds.ITIN
    ? NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.ITIN]?.name
    : NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.SSN]?.name;
});

const { handleChange, value, errors, validate, resetField, setValue } = useField(
  props.schema.name || "nationalIdNumber",
  props.rules || yup.string(),
);

function formatNationalIdNumber(nationalIdNumber: string): string | undefined {
  if (isNationalIdNumberMasked(nationalIdNumber)) {
    return nationalIdNumber;
  }
  // Automatically format the SSN as the user types
  if (nationalIdNumber && !isNationalIdNumberMasked(nationalIdNumber)) {
    // Remove non-numeric characters
    let digits = nationalIdNumber.replace(/\D/g, "");

    // Limit to 9 digits (standard SSN length)
    if (digits.length > 9) {
      digits = digits.slice(0, 9);
    }

    let formattedValue = digits;
    // Apply SSN format: XXX-XX-XXXX
    if (digits.length > 5) {
      formattedValue = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    } else if (digits.length > 3) {
      formattedValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      formattedValue = digits;
    }

    return formattedValue;
  }

  return undefined;
}

function formatInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  const cleanValue = `${value}`.replace(/[^0-9]/g, "");

  if (!value) {
    setValue(undefined);
    return;
  }

  setValue(formatNationalIdNumber(cleanValue));
}

function handleBlur() {
  isFocused.value = false;
  validate();
}

function handleKeyUp(event: Event) {
  formatInput(event);
}

function toggleNationalIdNumber() {
  //#TODO call endpoint to retrieve the unmasked one
  isMasked.value = !isMasked.value;
}

const ignoreKeys: string[] = [
  "Backspace",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
  "Enter",
  "Delete",
  "Shift",
  "CapsLock",
  "Home",
  "End",
];

function validateSsnInput(event: any) {
  // 1. Allow structural, control, and navigation keys (Backspace, Delete, Tab, Escape, Enter, Arrow keys)
  const isControlKey = ignoreKeys.indexOf(event.key) >= 0;

  // 2. Allow keyboard shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
  const isShortcut =
    (event.ctrlKey || event.metaKey) && ["a", "c", "v", "x"].includes(event.key.toLowerCase());

  if (isControlKey || isShortcut) {
    return;
  }
  // 3. Block the key if it is not a digit (0-9) or a dash
  const isNumberOrHyphen = /^[0-9-]$/.test(event.key);
  const validDigit = /^[0-9]$/.test(event.key) || /^[-]$/.test(event.key);

  // 4. Do not allow more than 9 numbers
  const cleanedValue = event.target.value?.replace(/[^0-9]/g, "");

  if (!isNumberOrHyphen || cleanedValue.length >= 9) {
    event.preventDefault();
  }
}

function handleInput(event: Event) {
  // Handle edge cases where input is autofill or pasted
  const element = event.target as HTMLInputElement;

  // Global replacement of everything that isn't a number or a hyphen
  element.value = element.value.replace(/[^0-9]/g, "");
  const value = element?.value;

  // format it if there are 9 digits
  if (value && value.length === 9) {
    handleChange(formatNationalIdNumber(value));
  } else {
    handleChange(value);
  }
}

watch(modelValue, (newValue, oldValue) => {
  if (
    (oldValue === "" || oldValue === null || oldValue === undefined) &&
    (newValue === "" || newValue === null || newValue === undefined)
  ) {
    setValue(null, false);
    return;
  }
  if (newValue === "" || newValue === null || newValue === undefined) {
    setValue(null);
  } else {
    setValue(value.value, false);
  }
});

watch(value, () => {
  if (value.value && typeof value.value === "string") {
    emit("update:modelValue", formatNationalIdNumber(value.value));
  } else {
    emit("update:modelValue", undefined);
  }
});

onMounted(() => {
  if (props.modelValue) {
    setValue(value.value, false);
  }
});
__expose({
  value,
  errors,
  formatNationalIdNumber,
  maskedValue,
  rawValue,
  isNationalIdNumberMasked,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _renderSlot(_ctx.$slots, "label", {}, () => [
      _createElementVNode("label", _hoisted_2, [
        _createTextVNode(_toDisplayString(__props.schema.label ? __props.schema.label : " "), 1),
        (__props.schema.label && __props.schema.required && __props.schema.showRequiredIndicator)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("input", _mergeProps(_ctx.$attrs, {
        ref: "inputRef",
        type: "text",
        name: "nationalIdNumber",
        id: "nationalIdNumber",
        pattern: "[0-9\\-X]+",
        inputmode: "numeric",
        autocomplete: "off",
        disabled: __props.disabled,
        onKeyup: handleKeyUp,
        onKeydown: validateSsnInput,
        value: displayValue.value,
        onFocus: _cache[0] || (_cache[0] = ($event: any) => (isFocused.value = true)),
        onBlur: handleBlur,
        onInput: _cache[1] || (_cache[1] = ($event: any) => (handleInput($event))),
        class: ["text-input", [
          'form-control',
          {
            'is-invalid': __props.schema.touched && !__props.schema.valid,
          },
        ]]
      }), null, 16, _hoisted_5),
      (showNationalIdNumberToggle.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            onClick: toggleNationalIdNumber,
            class: "eye-btn",
            "aria-label": "Toggle SSN visibility"
          }, [
            _createVNode(_unref(FontAwesomeIcon), {
              class: "toggle",
              icon: ['fas', toggleIcon.value]
            }, null, 8, ["icon"])
          ]))
        : _createCommentVNode("", true)
    ]),
    (__props.showItinIndicator)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "inline-checkbox-container",
          disabled: __props.disabled
        }, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            id: "itinIndicator",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((itinIndicator).value = $event)),
            "aria-label": "Toggle ITIN checkbox",
            onChange: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('update:itinIndicator', ($event.target as HTMLInputElement).checked)))
          }, null, 544), [
            [_vModelCheckbox, itinIndicator.value]
          ]),
          _cache[4] || (_cache[4] = _createElementVNode("label", { for: "itinIndicator" }, "Indicates ITIN", -1))
        ], 8, _hoisted_6))
      : _createCommentVNode("", true),
    (_unref(errors) && _unref(errors).length >= 1 && !__props.disabled)
      ? (_openBlock(), _createElementBlock("ul", {
          key: 1,
          class: _normalizeClass(["invalid-feedback", {
        multiple: _unref(errors) && _unref(errors).length > 1,
      }])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(errors), (error, index) => {
            return (_openBlock(), _createElementBlock("li", { key: index }, _toDisplayString(error), 1))
          }), 128))
        ], 2))
      : _createCommentVNode("", true)
  ]))
}
}

})